import { API_BASE } from '../../apiBase';
// src/contracts/components/ExhibitDetail.js
// Renders the editor for whichever exhibit the user picked in the navigator.
// Lives in the central column of Contract.js when activeView !== 'body'.
//
// Per-kind dispatch:
//   gli, wc                  → read-only display of CompanySettings values
//                              + link to /settings/company to change them
//   cancel_3day / cancel_7day → editable boilerplate + cancel-by-date input
//                              (auto-defaults to effective date + N business days)
//   change_order             → editable boilerplate
//   material_list            → full-width ContractTableEditor (+ paste-from-Sheets)
//   custom                   → paragraph editor + optional table editor
//   uploaded_pdf             → inline iframe (browser's native PDF viewer) +
//                              Replace file button

import React, { useEffect, useState, useRef } from 'react';
import { ExternalLink, FileText, AlertTriangle, Upload, Loader2 } from 'lucide-react';

import ContractTableEditor from './ContractTableEditor';
import ProseBlock, { BulletsBlock, EditableHeading, RegulatedBadge } from './ProseBlock';
import api from '../api';

const letterOf = (i) => String.fromCharCode(65 + i);

// Add N business days (skip Sat/Sun) to a date. Used to compute the default
// cancel-by date for the 3-day and 7-day notices.
function addBusinessDays(d, n) {
  const out = new Date(d);
  let added = 0;
  while (added < n) {
    out.setDate(out.getDate() + 1);
    const dow = out.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return out;
}
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function isoToMDY(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-US'); } catch (_) { return iso; }
}

// ============================================================================
// Per-kind editors
// ============================================================================

function GliExhibit({ company }) {
  const gl = company?.generalLiability || {};
  const tick = on => on ? '☒' : '☐';
  return (
    <div className="space-y-3 text-[13px] text-zinc-200 leading-relaxed">
      <p>Contractor must check the box that applies. <span className="text-zinc-500">Values pulled from Settings → Company.</span></p>
      <div className="space-y-2 pl-3">
        <div>{tick(!gl.carries && !gl.selfInsured && !gl.isLLC)} This Contractor does not carry commercial general liability insurance.</div>
        <div>{tick(!!gl.carries)} This Contractor carries commercial general liability insurance written by <span className="underline">{gl.carrierName || '_________________'}</span>. You may call the Insurance Company at <span className="underline">{gl.carrierPhone || '____________'}</span> to check Contractor's insurance coverage.</div>
        <div>{tick(!!gl.selfInsured)} This Contractor is self-insured.</div>
        <div>{tick(!!gl.isLLC)} This Contractor is a limited liability company that carries liability insurance or maintains other security as required by law.</div>
      </div>
      <a
        href="/settings/company"
        className="inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-indigo-200 underline-offset-2 hover:underline"
      >
        Edit in Settings → Company <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function WcExhibit({ company }) {
  const wc = company?.workersComp || {};
  const tick = on => on ? '☒' : '☐';
  return (
    <div className="space-y-3 text-[13px] text-zinc-200 leading-relaxed">
      <p>Contractor must check the box that applies. <span className="text-zinc-500">Values pulled from Settings → Company.</span></p>
      <div className="space-y-2 pl-3">
        <div>{tick(!!wc.exempt)} This Contractor has no employees and is exempt from workers' compensation requirements.</div>
        <div>{tick(!!wc.carries)} This Contractor carries workers' compensation insurance for all employees.</div>
      </div>
      <a
        href="/settings/company"
        className="inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-indigo-200 underline-offset-2 hover:underline"
      >
        Edit in Settings → Company <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
}

function CancelNoticeExhibit({ exhibit, days, contractor, effectiveDate, onChange }) {
  // Auto-default the cancel-by date if not set
  const defaultCancelBy = (() => {
    try {
      const base = effectiveDate ? new Date(effectiveDate) : new Date();
      return addBusinessDays(base, days).toLocaleDateString('en-US');
    } catch (_) { return ''; }
  })();
  const cancelBy = exhibit.data?.cancelByDate || defaultCancelBy;

  const updateCancelBy = (val) => {
    onChange({ ...exhibit, data: { ...(exhibit.data || {}), cancelByDate: val } });
  };
  const updateParagraphs = (paragraphs) => {
    onChange({ ...exhibit, data: { ...(exhibit.data || {}), paragraphs } });
  };

  const defaultParagraphs = [
    'This page is only applicable if this Contract was not negotiated at the Contractor\'s place of business. If so, the Owner is to receive two (2) copies.',
    `You may cancel this transaction, without any penalty or obligation, within ${days === 7 ? 'seven (7)' : 'three (3)'} business days from the date the last party signed the Contract.`,
    'If you cancel, any property traded in, any payments made by you under the contract or sale, and any negotiable instrument executed by you will be returned within 10 days following receipt by the seller of your cancellation notice, and any security interest arising out of the transaction will be canceled.',
  ];
  const paragraphs = exhibit.data?.paragraphs || defaultParagraphs;

  return (
    <div className="space-y-4 text-[13px] text-zinc-200">
      <ProseBlock
        heading={null}
        paragraphs={paragraphs}
        exhibits={[]}
        onChangeParagraphs={updateParagraphs}
      />

      <div className="rounded-lg border border-zinc-800 bg-gray-950/40 p-4 space-y-3">
        <div className="text-xs uppercase tracking-wider text-zinc-500">Mailing instructions</div>
        <p className="text-[13px] leading-relaxed">
          To cancel this transaction, mail or deliver a signed and dated copy of this cancellation notice
          to <span className="underline text-zinc-100">{contractor?.nameLine || '________________'}</span>
          at <span className="underline text-zinc-100">{contractor?.addressLine || '________________________________'}</span>.
        </p>
        <div className="flex items-center gap-3 pt-2">
          <label className="text-xs uppercase tracking-wider text-zinc-400">not later than midnight of</label>
          <input
            type="text"
            value={cancelBy}
            onChange={(e) => updateCancelBy(e.target.value)}
            placeholder={defaultCancelBy}
            className="bg-gray-950 border border-zinc-800 rounded px-2 py-1 text-sm text-zinc-100 underline focus:outline-none focus:border-indigo-500"
          />
          <button
            type="button"
            onClick={() => updateCancelBy(defaultCancelBy)}
            className="text-[11px] text-zinc-500 hover:text-indigo-300"
            title="Reset to effective date + business days"
          >
            reset
          </button>
        </div>
        <p className="text-[11px] text-zinc-500">
          Defaults to the effective date + {days} business days. Edit the field to override.
        </p>
      </div>
    </div>
  );
}

function ChangeOrderExhibit({ exhibit, onChange }) {
  const defaultParagraphs = [
    'A. Scope of work encompassed by this change order:',
    'B. Amount to be added or subtracted from the Contract price:',
    'C. Effect this change order will have on progress payments (if applicable):',
    'D. Effect this change order will have on the estimated completion date:',
  ];
  const paragraphs = exhibit.data?.paragraphs || defaultParagraphs;
  return (
    <div className="space-y-3 text-[13px] text-zinc-200">
      <ProseBlock
        heading={null}
        paragraphs={paragraphs}
        exhibits={[]}
        onChangeParagraphs={(p) => onChange({ ...exhibit, data: { ...(exhibit.data || {}), paragraphs: p } })}
      />
      <div className="rounded-lg border border-zinc-800 bg-gray-950/40 p-4 text-xs text-zinc-500">
        Signature block (Contractor / Owner) is rendered automatically in the PDF.
      </div>
    </div>
  );
}

function MaterialListExhibit({ exhibit, onChange }) {
  const table = exhibit.data?.table || {
    columns: [
      { header: 'Item', width: 4 },
      { header: 'Total', width: 1 },
      { header: 'Make', width: 2 },
      { header: 'Model', width: 3 },
    ],
    rows: [['', '', '', '']],
  };

  // Paste from Sheets: TSV in the clipboard → repopulate the table
  const onPaste = (e) => {
    const text = (e.clipboardData || window.clipboardData).getData('text');
    if (!text || !text.includes('\t')) return;
    e.preventDefault();
    const lines = text.split(/\r?\n/).filter(Boolean);
    const rows = lines.map(l => l.split('\t'));
    const ncols = Math.max(...rows.map(r => r.length));
    const columns = (table.columns?.length === ncols)
      ? table.columns
      : Array.from({ length: ncols }).map((_, i) => ({ header: `Col ${i + 1}`, width: 1 }));
    onChange({ ...exhibit, data: { ...(exhibit.data || {}), table: { columns, rows } } });
  };

  return (
    <div className="space-y-2" onPaste={onPaste}>
      <p className="text-[11px] text-zinc-500">
        Tip: copy a range in Google Sheets and paste anywhere on this card to overwrite the table.
      </p>
      <ContractTableEditor
        value={table}
        onChange={(t) => onChange({ ...exhibit, data: { ...(exhibit.data || {}), table: t } })}
      />
    </div>
  );
}

function CustomExhibit({ exhibit, onChange }) {
  const paragraphs = exhibit.data?.paragraphs || [''];
  const table = exhibit.data?.table;
  return (
    <div className="space-y-4">
      <ProseBlock
        heading={null}
        paragraphs={paragraphs}
        exhibits={[]}
        onChangeParagraphs={(p) => onChange({ ...exhibit, data: { ...(exhibit.data || {}), paragraphs: p } })}
      />
      {table ? (
        <ContractTableEditor
          value={table}
          onChange={(t) => onChange({ ...exhibit, data: { ...(exhibit.data || {}), table: t } })}
        />
      ) : (
        <button
          type="button"
          onClick={() => onChange({
            ...exhibit,
            data: {
              ...(exhibit.data || {}),
              table: { columns: [{ header: 'A', width: 1 }, { header: 'B', width: 1 }], rows: [['', ''], ['', '']] },
            },
          })}
          className="text-xs text-indigo-300 hover:text-indigo-200"
        >
          + add a table to this exhibit
        </button>
      )}
    </div>
  );
}

// ----- Uploaded PDF viewer (native browser PDF viewer via iframe) -----
// Way simpler than react-pdf: zero dependencies, zero worker setup, instant
// render. The browser's built-in PDF viewer comes with page nav, zoom, search,
// print, and download in its native toolbar — all for free.
function UploadedPdfExhibit({ exhibit, contractId, onChange }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const fileRef = useRef(null);

  // Fetch the file from the backend (auth required), blob it, hand the blob
  // URL to an iframe. Browser's PDF viewer takes it from there.
  useEffect(() => {
    let mounted = true;
    let blobUrl;
    (async () => {
      if (!contractId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      setLoadError(null);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `${API_BASE}/contracts/${contractId}/exhibit/${exhibit.exhibitId}/file`,
          { headers: { Authorization: token } }
        );
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
        const blob = await res.blob();
        blobUrl = URL.createObjectURL(blob);
        if (mounted) setPdfUrl(blobUrl);
      } catch (err) {
        console.warn('Could not load exhibit file', err);
        if (mounted) setLoadError(err.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [contractId, exhibit.exhibitId, exhibit.tempPath]);

  const handleReplace = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !contractId) return;
    try {
      const res = await api.uploadExhibit(contractId, exhibit.exhibitId, file);
      onChange({
        ...exhibit,
        title: file.name,
        originalFileName: res.originalFileName,
        pageCount: res.pageCount,
        tempPath: res.tempPath,
      });
    } catch (err) {
      alert('Replace failed: ' + (err.response?.data?.error || err.message));
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-xs text-zinc-400">
        <div className="flex items-center gap-2 min-w-0">
          <FileText className="w-3.5 h-3.5 text-indigo-300 flex-shrink-0" />
          <span className="truncate">{exhibit.originalFileName || exhibit.title}</span>
          {exhibit.pageCount ? (
            <span className="text-zinc-600 flex-shrink-0">
              · {exhibit.pageCount} {exhibit.pageCount === 1 ? 'page' : 'pages'}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="text-indigo-300 hover:text-indigo-200 inline-flex items-center gap-1 flex-shrink-0 ml-3"
        >
          <Upload className="w-3 h-3" /> replace file
        </button>
        <input ref={fileRef} type="file" accept="application/pdf" onChange={handleReplace} className="hidden" />
      </div>

      <div className="rounded-lg border border-zinc-800 bg-gray-950 overflow-hidden min-h-[600px] relative">
        {/* Loading state — visible until the blob URL is ready */}
        {loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-zinc-500 text-sm bg-gray-950">
            <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
            <span>Loading {exhibit.originalFileName || 'PDF'}…</span>
          </div>
        )}

        {/* Error state — typically means the temp file has been swept */}
        {!loading && loadError && (
          <div className="p-4 m-2 rounded-lg border border-amber-800/50 bg-amber-950/20 text-xs text-amber-200">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4" />
              Couldn't load this PDF.
            </div>
            <p className="text-amber-300/80">
              {loadError}. The file may have been cleaned out of the temp directory
              after a previous Generate. Use the "replace file" button above to re-upload.
            </p>
          </div>
        )}

        {/* The native PDF viewer — page navigation, zoom, search, print, and
            download are all available in the iframe's built-in toolbar. */}
        {!loading && !loadError && pdfUrl && (
          <iframe
            src={pdfUrl}
            title={exhibit.title || 'Exhibit PDF'}
            className="w-full h-[700px] border-0 bg-white"
          />
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main component
// ============================================================================

export default function ExhibitDetail({
  contractId,
  exhibit,
  exhibitIndex,
  company,
  contractor,
  effectiveDate,
  onChange,
  onClose,
}) {
  if (!exhibit) {
    return null;
  }
  const letter = letterOf(exhibitIndex);

  const renderContent = () => {
    switch (exhibit.kind) {
      case 'gli': return <GliExhibit company={company} />;
      case 'wc':  return <WcExhibit company={company} />;
      case 'cancel_3day':
        return <CancelNoticeExhibit exhibit={exhibit} days={3} contractor={contractor} effectiveDate={effectiveDate} onChange={onChange} />;
      case 'cancel_7day':
        return <CancelNoticeExhibit exhibit={exhibit} days={7} contractor={contractor} effectiveDate={effectiveDate} onChange={onChange} />;
      case 'change_order':
        return <ChangeOrderExhibit exhibit={exhibit} onChange={onChange} />;
      case 'material_list':
        return <MaterialListExhibit exhibit={exhibit} onChange={onChange} />;
      case 'custom':
        return <CustomExhibit exhibit={exhibit} onChange={onChange} />;
      case 'uploaded_pdf':
        return <UploadedPdfExhibit exhibit={exhibit} contractId={contractId} onChange={onChange} />;
      default:
        return <div className="text-xs text-zinc-500">Unknown exhibit type: {exhibit.kind}</div>;
    }
  };

  return (
    <div className="bg-gray-900 rounded-2xl border border-gray-800/50 p-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1">
            Exhibit {letter}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <EditableHeading
              value={exhibit.title}
              onChange={(title) => onChange({ ...exhibit, title })}
            />
            {exhibit.regulated && <RegulatedBadge />}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs text-zinc-400 hover:text-zinc-100 inline-flex items-center gap-1"
        >
          ← back to contract body
        </button>
      </div>
      <div className="pt-2 border-t border-zinc-800/50">
        {renderContent()}
      </div>
    </div>
  );
}
